import { PLATFORM } from 'aurelia-framework';
import { activationStrategy } from 'aurelia-router';
import { RoleEnum } from '../../classes/role-enum';
import { IRouteConfig } from '../../interfaces/i-route-config';
import { Route } from './route';
import { CategoryType } from '../../classes/category-type';

export const categoryRoute: IRouteConfig = {
  route: [`/${Route.categoryTypes}`,],
  name: Route.categoryTypes,
  settings: {
    section: 'customer',
    icon: 'category',
    rolesWithAccess: [RoleEnum.Admin, RoleEnum.CustomerAdmin]
  },
  moduleId: PLATFORM.moduleName('../category-types/category-types', 'category-types-module'),
  title: 'Categories'
};

export const categoriesRoute: IRouteConfig = {
  route: Route.categories,
  routeParams: { subRoute: CategoryType.Portfolio },
  name: Route.categories,
  moduleId: PLATFORM.moduleName('../categories/categories', 'categories-module'),
  title: 'Portfolio categories',
};

export const customerSettingsRoute: IRouteConfig = {
  route: Route.customerSettings,
  name: Route.customerSettings,
  settings: {
    rolesWithAccess: [RoleEnum.Admin, RoleEnum.CustomerAdmin],
    icon: 'business'
  },
  moduleId: PLATFORM.moduleName('../customer/customer', 'customers-module'),
  title: 'Company settings'
};

export const bankAccountSettingsRoute: IRouteConfig = {
  route: Route.bankAccountSettings,
  name: Route.bankAccountSettings,
  settings: {
    rolesWithAccess: [RoleEnum.Admin, RoleEnum.BetaTest, RoleEnum.CustomerAdmin],
    icon: 'business'
  },
  moduleId: PLATFORM.moduleName('../bank-account-settings/bank-account-settings', 'bank-account-settings-module'),
  title: 'Bank account settings'
};

export const invoiceAdminConfigs: IRouteConfig[] = [
  {
    route: [`/${Route.activeCompanies}`,],
    name: Route.activeCompanies,
    settings: {
      section: 'admin',
      icon: 'business',
      rolesWithAccess: [RoleEnum.Admin]
    },
    moduleId: PLATFORM.moduleName('../active-companies/active-companies', 'active-companies'),
    title: 'Active companies',
    activationStrategy: activationStrategy.replace
  },
  {
    route: `/${Route.licenses}`,
    name: Route.licenses,
    settings: {
      section: 'admin',
      icon: 'key',
      rolesWithAccess: [RoleEnum.Admin]
    },
    moduleId: PLATFORM.moduleName('../licenses/licenses', 'licenses-module'),
    title: 'Licenses',
    activationStrategy: activationStrategy.replace
  },
  {
    route: `/${Route.packages}`,
    name: Route.packages,
    settings: {
      section: 'admin',
      icon: 'inventory_2',
      rolesWithAccess: [RoleEnum.Admin]
    },
    moduleId: PLATFORM.moduleName('../packages/packages-list/packages-list', 'packages-module'),
    title: 'Packages',
    activationStrategy: activationStrategy.replace
  },
  {
    route: `/${Route.partners}`,
    name: Route.partners,
    settings: {
      section: 'admin',
      icon: 'handshake',
      rolesWithAccess: [RoleEnum.Admin]
    },
    moduleId: PLATFORM.moduleName('../partners/partners-list/partners-list', 'partners-module'),
    title: 'Partners',
    activationStrategy: activationStrategy.replace
  },
  {
    route: `/${Route.licenseOwners}`,
    name: Route.licenseOwners,
    settings: {
      section: 'admin',
      icon: 'badge',
      rolesWithAccess: [RoleEnum.Admin]
    },
    moduleId: PLATFORM.moduleName('../license-owners/license-owners-list/license-owners-list', 'license-owners-module'),
    title: 'License Owners',
    activationStrategy: activationStrategy.replace
  },
  {
    route: `/${Route.invoicesList}`,
    name: 'invoicing',
    settings: {
      section: 'admin',
      icon: 'receipt',
      rolesWithAccess: [RoleEnum.Admin]
    },
    moduleId: PLATFORM.moduleName('../invoicing/invoicing', 'invoicing-module'),
    title: 'Invoicing',
    activationStrategy: activationStrategy.replace
  },
];

export const adminConfigs: IRouteConfig[] = [
  {
    route: `/${Route.editReportPages}`,
    name: Route.editReportPages,
    settings: {
      section: 'admin',
      icon: 'analytics',
      rolesWithAccess: [RoleEnum.Admin]
    },
    moduleId: PLATFORM.moduleName('../report-pages/report-pages', 'report-pages-module'),
    title: 'Edit report pages',
    activationStrategy: activationStrategy.replace
  },
  {
    route: `/${Route.editReports}`,
    name: Route.editReports,
    settings: {
      section: 'admin',
      icon: 'analytics',
      rolesWithAccess: [RoleEnum.Admin]
    },
    moduleId: PLATFORM.moduleName('../edit-reports/edit-reports', 'edit-reports-module'),
    title: 'Edit reports',
    activationStrategy: activationStrategy.replace
  },
  {
    route: ['/users/'],
    name: 'users',
    nav: true,
    settings: {
      rolesWithAccess: [RoleEnum.Admin],
      section: 'admin',
      icon: 'people'
    },
    moduleId: PLATFORM.moduleName('../users/users', 'users-module'),
    title: 'Users'
  },
  {
    route: ['/customers/'],
    name: Route.customers,
    nav: true,
    settings: {
      rolesWithAccess: [RoleEnum.Admin],
      section: 'admin',
      icon: 'business'
    },
    moduleId: PLATFORM.moduleName('../customers/customers', 'customers-module'),
    title: 'Customers'
  },
  {
    route: ['/paths/'],
    name: Route.paths,
    nav: true,
    settings: {
      rolesWithAccess: [RoleEnum.Admin],
      section: 'admin',
      icon: 'alt_route'
    },
    moduleId: PLATFORM.moduleName('../paths/paths', 'paths-module'),
    title: 'Paths'
  },
  {
    route: Route.permissions,
    name: Route.permissions,
    nav: true,
    settings: {
      rolesWithAccess: [RoleEnum.Admin],
      section: 'admin',
      icon: 'group_work'
    },
    moduleId: PLATFORM.moduleName('../permissions/permissions', 'permissions-module'),
    title: 'Permissions'
  },
  {
    route: Route.rolePermissions,
    name: Route.rolePermissions,
    nav: true,
    settings: {
      rolesWithAccess: [RoleEnum.Admin],
      section: 'admin',
      icon: 'thumbs_up_down'
    },
    moduleId: PLATFORM.moduleName('../role-permissions/role-permissions', 'role-permissions-module'),
    title: 'Role permissions'
  },
  {
    route: ['/modules/'],
    name: Route.modules,
    nav: true,
    settings: {
      rolesWithAccess: [RoleEnum.Admin],
      section: 'admin',
      icon: 'view_module'
    },
    moduleId: PLATFORM.moduleName('../modules/modules', 'modules-module'),
    title: 'Modules'
  },
  {
    route: ['/licenses/'],
    name: Route.licenses,
    nav: true,
    settings: {
      rolesWithAccess: [RoleEnum.Admin],
      section: 'admin',
      icon: 'paid'
    },
    moduleId: PLATFORM.moduleName('../licenses/licenses', 'licenses-module'),
    title: 'Licenses'
  },
  {
    route: Route.externalData,
    name: Route.externalData,
    nav: true,
    settings: {
      rolesWithAccess: [RoleEnum.Admin],
      section: 'admin',
      icon: 'upload'
    },
    moduleId: PLATFORM.moduleName('../external-data/external-data', 'external-data-module'),
    title: 'External data'
  },
  {
    route: Route.eula,
    name: Route.eula,
    nav: true,
    settings: {
      rolesWithAccess: [RoleEnum.Admin],
      section: 'admin',
      icon: 'upload'
    },
    moduleId: PLATFORM.moduleName('../eula/eula', 'eula-module'),
    title: 'Eula'
  },
  {
    route: [`/${Route.reconcileSummary}`,],
    name: Route.reconcileSummary,
    settings: {
      section: 'admin',
      icon: 'join_inner',
      rolesWithAccess: [RoleEnum.Admin, RoleEnum.CustomerAdmin]
    },
    moduleId: PLATFORM.moduleName('../reconcile-summary/reconcile-summary', 'reconcile-summary-module'),
    title: 'Reconcile summary'
  },
  {
    route: [`/${Route.reconcile}`,],
    name: Route.reconcile,
    settings: {
      section: 'admin',
      icon: 'join_inner',
      rolesWithAccess: [RoleEnum.Admin, RoleEnum.CustomerAdmin]
    },
    moduleId: PLATFORM.moduleName('../reconcile/reconcile', 'reconcile-module'),
    title: 'Reconcile (old)'
  },
  {
    route: [`/${Route.mandateControl}`,],
    name: Route.mandateControl,
    settings: {
      section: 'admin',
      rolesWithAccess: [RoleEnum.Admin, RoleEnum.CustomerAdmin]
    },
    moduleId: PLATFORM.moduleName('../mandate-control/mandate-control', 'mandate-control-module'),
    title: 'Mandate сontrol'
  },
  {
    route: [`/${Route.corporations}`,],
    name: Route.corporations,
    settings: {
      section: 'admin',
      icon: 'business',
      rolesWithAccess: [RoleEnum.Admin]
    },
    moduleId: PLATFORM.moduleName('../corporations/corporations', 'corporations-module'),
    title: 'Corporations'
  }
];


export const customerConfigs: IRouteConfig[] = [
  {
    route: ['/transactions-overview/'],
    name: 'transactions-overview',
    nav: true,
    settings: {
      section: 'customer',
      icon: 'receipt_long',
      rolesWithAccess: [RoleEnum.Admin, RoleEnum.CustomerAdmin, RoleEnum.Customer]
    },
    activationStrategy: activationStrategy.replace,
    moduleId: PLATFORM.moduleName('../transactions-overview/overview-wrapper', 'overview-module'),
    title: 'Transactions overview',
    fullscreen: true
  },
  {
    route: `/${Route.reportPages}`,
    name: Route.reportPages,
    settings: {
      section: 'customer',
      icon: 'analytics',
      rolesWithAccess: [RoleEnum.Admin, RoleEnum.CustomerAdmin, RoleEnum.Customer]
    },
    moduleId: PLATFORM.moduleName('../report-pages/report-pages', 'report-pages-module'),
    title: 'Report pages',
    activationStrategy: activationStrategy.replace
  },
  {
    route: `/${Route.reports}`,
    name: Route.reports,
    settings: {
      section: 'customer',
      icon: 'analytics',
      rolesWithAccess: [RoleEnum.Admin, RoleEnum.CustomerAdmin, RoleEnum.Customer]
    },
    moduleId: PLATFORM.moduleName('../reports/reports', 'reports-module'),
    title: 'View reports',
    activationStrategy: activationStrategy.replace
  },
  {
    route: [`/${Route.notifications}`,],
    name: Route.notifications,
    settings: {
      section: 'customer',
      icon: 'notifications',
      rolesWithAccess: [RoleEnum.Admin, RoleEnum.CustomerAdmin, RoleEnum.Customer]
    },
    moduleId: PLATFORM.moduleName('../notifications/notifications', 'notifications-module'),
    title: 'Notifications'
  },
  {
    route: [`/${Route.reconcileNew}`,],
    name: Route.reconcileNew,
    settings: {
      section: 'admin',
      icon: 'join_inner',
      rolesWithAccess: [RoleEnum.Admin, RoleEnum.CustomerAdmin]
    },
    moduleId: PLATFORM.moduleName('../reconcile-new/reconcile-new', 'reconcile-module'),
    title: 'Reconcile'
  },
  {
    route: [`/${Route.openingPositions}`,],
    name: Route.openingPositions,
    moduleId: PLATFORM.moduleName('../opening-positions/opening-positions', 'opening-positions-module'),
    title: 'Opening positions'
  }
];

export const routeConfigs: IRouteConfig[] = [
  ...adminConfigs,
  ...invoiceAdminConfigs,
  customerSettingsRoute,
  bankAccountSettingsRoute,
  ...customerConfigs,
  {
    route: ['', '/home/'],
    name: 'home',
    nav: false,
    moduleId: PLATFORM.moduleName('../home/home', 'home-module'),
    title: 'Home'
  },
  {
    route: ['/login/'],
    name: 'login',
    nav: false,
    activationStrategy: activationStrategy.replace,
    moduleId: PLATFORM.moduleName('../login/login', 'login-module'),
    title: 'login'
  },
  {
    route: ['/overview/'],
    name: 'overview',
    nav: true,
    activationStrategy: activationStrategy.replace,
    moduleId: PLATFORM.moduleName('../overview/overview', 'overview-module'),
    title: 'Overview',
    fullscreen: true
  },
  {
    route: ['/login/'],
    name: 'login',
    moduleId: PLATFORM.moduleName('../login/login', 'login-module'),
    title: 'login'
  },
  {
    route: ['/processed-vouchers-detailed'],
    name: 'processed-vouchers-detailed',
    nav: true,
    moduleId: PLATFORM.moduleName('../processedVouchersDetailed/processedVoucherDetailedWrapper', 'processedVouchersDetailed-module'),
    title: 'Processed Vouchers Detailed'
  },
  {
    route: ['/processed-vouchers/', '/processed-vouchers/:instrumentId'],
    name: 'processed-vouchers',
    nav: true,
    href: '#',
    moduleId: PLATFORM.moduleName('../processedVouchersGrouped/processedVoucherGroupedWrapper', 'processedVouchersGrouped-module'),
    title: 'Processed Vouchers'
  },
  {
    route: ['/users/:id/',],
    name: 'user',
    settings: {
      rolesWithAccess: [RoleEnum.Admin],
    },
    moduleId: PLATFORM.moduleName('../user/user', 'users-module'),
    title: 'Edit user'
  },
  {
    route: ['/debug',],
    name: 'debug-info',
    settings: {
      rolesWithAccess: [RoleEnum.Admin],
    },
    moduleId: PLATFORM.moduleName('../debug-info/debug-info', 'debug-info'),
    title: 'Debug Info'
  },
  {
    route: '/customers/:id/',
    name: Route.customer,
    settings: {
      rolesWithAccess: [RoleEnum.Admin, RoleEnum.CustomerAdmin],
    },
    moduleId: PLATFORM.moduleName('../customer/customer', 'customers-module'),
    title: 'Edit customer'
  },
  {
    route: ['/instrument/:instrumentId/imported-prices', 'imported-prices/:availableInstrumentUuid'],
    name: 'imported-prices',
    settings: {},
    moduleId: PLATFORM.moduleName('../imported-prices/imported-prices', 'imported-prices-module'),
    title: 'Imported Prices'
  },
  {
    route: ['/instrument/:instrumentId/', '/instrument/:instrumentId/:tab',],
    name: 'instrument',
    activationStrategy: activationStrategy.replace,
    settings: {},
    moduleId: PLATFORM.moduleName('../instrument/instrument', 'instrument-module'),
    title: 'Instrument'
  },
  {
    route: ['/documents/:documentId/vouchers/:voucherId', '/documents/:documentId/voucher'],
    name: Route.upv,
    settings: {},
    activationStrategy: activationStrategy.replace,
    moduleId: PLATFORM.moduleName('../upv/upv', 'upv-module'),
    title: 'Unprocessed voucher'
  },
  {
    route: ['/documents/:voucherDocumentId/vouchers'],
    name: Route.unprocessedVoucherList,
    settings: {},
    activationStrategy: activationStrategy.replace,
    moduleId: PLATFORM.moduleName('../upvList/upvListWrapper', 'upvList-module'),
    title: 'Vouchers'
  },
  {
    route: [`/documents/:documentId/vouchers/grouped`],
    name: Route.upvsGrouped,
    settings: {},
    activationStrategy: activationStrategy.replace,
    moduleId: PLATFORM.moduleName('../upvs-grouped/upvs-grouped', 'upvs-grouped-module'),
    title: 'Grouped vouchers'
  },
  {
    route: ['/vouchers/favorite'],
    name: Route.unprocessedVoucherFavorite,
    settings: {},
    activationStrategy: activationStrategy.replace,
    moduleId: PLATFORM.moduleName('../upvList/upvListWrapper', 'upvList-module'),
    title: 'Favorite Vouchers'
  },
  {
    route: ['/vouchers/all'],
    name: Route.allVouchers,
    settings: {},
    activationStrategy: activationStrategy.replace,
    moduleId: PLATFORM.moduleName('../upvList/upvListWrapper', 'upvList-module'),
    title: 'All vouchers'
  },
  {
    route: [`/vouchers/favorite/grouped`],
    name: Route.favoriteVouchersGrouped,
    settings: {},
    activationStrategy: activationStrategy.replace,
    moduleId: PLATFORM.moduleName('../upvs-grouped/upvs-grouped', 'upvs-grouped-module'),
    title: 'Grouped favorite vouchers'
  },
  {
    route: [`/vouchers/all/grouped`],
    name: Route.allVouchersGrouped,
    settings: {},
    activationStrategy: activationStrategy.replace,
    moduleId: PLATFORM.moduleName('../upvs-grouped/upvs-grouped', 'upvs-grouped-module'),
    title: 'Grouped favorite vouchers'
  },
  {
    route: ['/documents'],
    name: 'documents',
    settings: {},
    activationStrategy: activationStrategy.replace,
    moduleId: PLATFORM.moduleName('../documents/documents', 'documents-module'),
    title: 'Documents'
  },
  categoryRoute,
  categoriesRoute,
  {
    route: '/forbidden',
    name: 'forbidden',
    moduleId: PLATFORM.moduleName('../forbidden/forbidden', 'forbidden-module'),
    title: 'Forbidden'
  },
  {
    route: ['/modules/:id/',],
    name: Route.module,
    settings: {
      rolesWithAccess: [RoleEnum.Admin],
    },
    moduleId: PLATFORM.moduleName('../module/module', 'modules-module'),
    title: 'Edit module',
    activationStrategy: activationStrategy.replace
  },

  {
    route: [Route.acceptEula],
    name: Route.acceptEula,
    moduleId: PLATFORM.moduleName('../accept-eula/accept-eula', 'accept-eula'),
    title: 'Accept EULA'
  },
  {
    route: [Route.customerAgreements],
    name: Route.customerAgreements,
    moduleId: PLATFORM.moduleName('../customer-agreements/customer-agreements', 'customer-agreements'),
    title: 'Customer licence agreements'
  },
  {
    route: Route.unconverted,
    name: Route.unconverted,
    moduleId: PLATFORM.moduleName('../unconverted/unconverted', 'unconverted-module'),
    title: 'Unconverted instruments'
  },
  {
    route: [`/${Route.glTransactions}/`],
    name: Route.glTransactions,
    nav: true,
    settings: {
      section: 'admin',
      icon: 'receipt_long',
      rolesWithAccess: [RoleEnum.Admin]
    },
    activationStrategy: activationStrategy.replace,
    moduleId: PLATFORM.moduleName('../gl-transactions/gl-transactions', 'gl-transactions-module'),
    title: 'GL transactions',
    fullscreen: true
  },
  {
    route: [`/${Route.settings}/`],
    name: Route.settings,
    nav: true,
    settings: {
      rolesWithAccess: [RoleEnum.Admin, RoleEnum.CustomerAdmin]
    },
    activationStrategy: activationStrategy.replace,
    moduleId: PLATFORM.moduleName('../settings/settings', 'settings-module'),
    title: 'Settings'
  },
  {
    route: ['/categories/type/:subRoute'],
    name: 'categories',
    activationStrategy: activationStrategy.replace,
    moduleId: PLATFORM.moduleName('../categories/categories', 'categories-module'),
    title: 'Categories',
    settings: {
      rolesWithAccess: [RoleEnum.Admin, RoleEnum.CustomerAdmin]
    }
  },
  {
    route: [`/${Route.taxValues}`],
    name: Route.taxValues,
    nav: true,
    settings: {
      rolesWithAccess: [RoleEnum.Admin, RoleEnum.CustomerAdmin]
    },
    activationStrategy: activationStrategy.replace,
    moduleId: PLATFORM.moduleName('../tax-values/tax-values', 'tax-values-module'),
    title: 'Tax values'
  },
];

